/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { usePostnatalMaternal } from "./hooks/usePostnatalMaternal";
import { usePostnatalNewborn } from "./hooks/usePostnatalNewborn";
import { PostnatalMaternalForm } from "./PostnatalMaternalForm";
import { PostnatalNewbornForm } from "./PostnatalNewbornForm";
import { PNCScheduleTracker } from "./PNCScheduleTracker";
import { PNCHistoryTab } from "./PNCHistoryTab";
import {
    PNCPageWrapper, PNCHeader, PNCHeaderLeft, PNCHeaderTitle, PNCHeaderSub,
    PNCHeaderStats, PNCStatChip,

    PNCLoading, PNCEmptyState, PNCEmptyIcon, PNCEmptyTitle, PNCEmptyDesc,
    PNCPrimaryBtn, PNCSecondaryBtn,
} from "./PostnatalCareStyles";
import { ObGynTabBar, ObGynTabBtn, ObGynTabBadge } from "../../Utils/ObGynTabStyles";
import { history } from "../../../../Helpers/history";

/* ═══════════════════════════════════════════════════════════════
   PostnatalCarePage — Combined page for Tasks 15 & 16
   Tabs: PNC Schedule | Maternal Assessment | Newborn Assessment
   Route: /gyn-and-obs-postnatal-care/:patient_number/:visit_id/:patient_type
   ═══════════════════════════════════════════════════════════════ */

const PostnatalCarePage = function (props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ── Stable ref for navigating to history after save ── */
    const setActiveTabRef = React.useRef(null);
    const goToHistory = React.useCallback(function () {
        if (setActiveTabRef.current) setActiveTabRef.current("history");
    }, []);

    /* ── Hooks ── */
    const maternal = usePostnatalMaternal(actions, patient_number, visit_id, goToHistory);
    const newbornPnc = usePostnatalNewborn(actions, patient_number, visit_id, maternal.deliveryRecord, goToHistory);

    const closeSnackbar = function () { actions.snackbarActions.hideSnackbar(); };

    /* ── Active tab — use maternal's tab state as primary ── */
    const activeTab = maternal.activeTab;
    const setActiveTab = maternal.setActiveTab;
    setActiveTabRef.current = setActiveTab;

    const goToDeliveryRecord = function () {
        history.push("/gyn-and-obs-delivery-record/" + patient_number + "/" + visit_id + "/" + patient_type);
    };

    /* ── Loading ── */
    if (maternal.loading) {
        return (
            <div>
                <PageTitle title="Postnatal Care" />
                <SubHeader title="OB/GYN" subTitle="Postnatal Care" />
                <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                    open={snackbar.open} handleCloseBar={closeSnackbar} />
                <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                    <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                    <PNCPageWrapper>
                        <PNCLoading>Loading postnatal care data...</PNCLoading>
                    </PNCPageWrapper>
                </ObGynSidebar>
            </div>
        );
    }

    /* ── No delivery record ── */
    if (!maternal.deliveryRecord) {
        return (
            <div>
                <PageTitle title="Postnatal Care" />
                <SubHeader title="OB/GYN" subTitle="Postnatal Care" />
                <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                    open={snackbar.open} handleCloseBar={closeSnackbar} />
                <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                    <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                    <PNCPageWrapper>
                        <PNCEmptyState>
                            <PNCEmptyTitle>No Delivery Record Found</PNCEmptyTitle>
                            <PNCEmptyDesc>
                                A delivery record must be created before postnatal care can begin.
                                Please document the delivery first in the Delivery & Birth Record section.
                            </PNCEmptyDesc>
                            <PNCPrimaryBtn onClick={goToDeliveryRecord}>
                                Go to Delivery Record
                            </PNCPrimaryBtn>
                        </PNCEmptyState>
                    </PNCPageWrapper>
                </ObGynSidebar>
            </div>
        );
    }

    /* ── Delivery summary chips ── */
    const delivery = maternal.deliveryRecord;
    const modeLabels = {
        svd: "SVD", vacuum: "Vacuum", forceps: "Forceps",
        emergency_cs: "Emergency CS", elective_cs: "Elective CS",
        breech_delivery: "Breech",
    };
    const deliveryModeLabel = modeLabels[delivery.delivery_mode] || delivery.delivery_mode;

    return (
        <div>
            <PageTitle title="Postnatal Care" />
            <SubHeader title="OB/GYN" subTitle="Postnatal Care" />
            <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                open={snackbar.open} handleCloseBar={closeSnackbar} />
            <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                <PNCPageWrapper>

                    {/* ─── Tab Bar ─── */}
                    <ObGynTabBar>
                        <ObGynTabBtn
                            active={activeTab === "schedule"}
                            onClick={function () { setActiveTab("schedule"); }}
                        >
                            PNC Schedule
                        </ObGynTabBtn>
                        <ObGynTabBtn
                            active={activeTab === "assessment"}
                            onClick={function () { setActiveTab("assessment"); }}
                        >
                            Maternal
                            <ObGynTabBadge active={activeTab === "assessment"}>
                                {maternal.pncVisits.length}
                            </ObGynTabBadge>
                        </ObGynTabBtn>
                        <ObGynTabBtn
                            active={activeTab === "newborn"}
                            onClick={function () { setActiveTab("newborn"); }}
                        >
                            Newborn
                            <ObGynTabBadge active={activeTab === "newborn"}>
                                {newbornPnc.pncVisits.length}
                            </ObGynTabBadge>
                        </ObGynTabBtn>
                        <ObGynTabBtn
                            active={activeTab === "history"}
                            onClick={function () { setActiveTab("history"); }}
                        >
                            History
                            <ObGynTabBadge active={activeTab === "history"}>
                                {maternal.pncVisits.length + newbornPnc.pncVisits.length}
                            </ObGynTabBadge>
                        </ObGynTabBtn>
                    </ObGynTabBar>

                    {/* ─── Tab Content ─── */}
                    {activeTab === "schedule" && (
                        <PNCScheduleTracker
                            schedule={maternal.scheduleWithStatus}
                            onViewVisit={function (visit) {
                                maternal.startEditVisit(visit);
                                setActiveTab("assessment");
                            }}
                            deliveryDate={delivery.delivery_datetime ? delivery.delivery_datetime.split("T")[0] : ""}
                        />
                    )}

                    {activeTab === "assessment" && (
                        <PostnatalMaternalForm maternal={maternal} />
                    )}

                    {activeTab === "newborn" && (
                        <PostnatalNewbornForm newbornPnc={newbornPnc} />
                    )}

                    {activeTab === "history" && (
                        <PNCHistoryTab
                            maternal={maternal}
                            newbornPnc={newbornPnc}
                            onEditMaternal={function (visit) {
                                maternal.startEditVisit(visit);
                                setActiveTab("assessment");
                            }}
                            onEditNewborn={function (visit) {
                                newbornPnc.startEditVisit(visit);
                                setActiveTab("newborn");
                            }}
                        />
                    )}

                </PNCPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

/* ─── Redux Connect ─── */
function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return { actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) } };
}

export default connect(mapStateToProps, mapDispatchToProps)(PostnatalCarePage);
